import { type FunctionComponent, useRef, useState } from 'react';

import { Button, ThemeProvider } from '@dr.pogodin/react-utils';

import PageWithVideoBackground from 'components/PageWithVideoBackground';
import CmsMenu from 'components/admin/Menu';
import NotificationsPanel from 'components/admin/NotificationsPanel';
import { P_API_DATA_EXPORT, useGlobalState } from 'utils';

import IconBell from 'assets/images/icon-bell-24px.svg';
import IconChecklist from 'assets/images/icon-checklist-20px.svg';
import IconCube from 'assets/images/icon-cube-20px.svg';

import IconDisplay from 'assets/images/icon-desktop-20px.svg';
import IconExport from 'assets/images/icon-export.svg';

import IconVideo from 'assets/images/icon-video-24px.svg';

import CheckedItem from './CheckedItem';
import CollapsibleTab from './CollapsibleTab';
import EquipmentItem from './EquipmentItem';
import GreatContentTips from './GreatContentTips';
import SupportAndResources from './SupportAndResources';
import WelcomeBanner from './WelcomeBanner';

import exportButtonTheme from './export-button-theme.scss';

import S from './style.scss';

// TODO: Should do permission check (it is done at the router level as well,
// but as a failsafe it is good to dupilcate the check here)?
const CmsDashboard: FunctionComponent = () => {
  const [user] = useGlobalState('user');

  if (!user?.permissions.CMS_APP) throw Error('Forbidden');

  const [tab, setTab] = useState<0 | 1 | 2>(0);

  const [isExpandedRaw] = useGlobalState('ui.adminSidebarExpanded');
  const isExpanded = isExpandedRaw !== false;

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);

  return (
    <PageWithVideoBackground
      noAudioSetup
      overlayClassName={`${S.pageContent} ${isExpanded ? S.expanded : ''}`}
      sidebar={<CmsMenu />}
    >
      <div styleName="mainColumn">
        <WelcomeBanner />

        {user.permissions.ADMIN_DATA_EXPORT ? (
          <div styleName="buttons">
            <ThemeProvider themes={{ Button: exportButtonTheme }}>
              <Button enforceA to={`${P_API_DATA_EXPORT}?kind=SESSIONS`}>
                <IconExport />
                Export sessions data
              </Button>
              <Button enforceA to={`${P_API_DATA_EXPORT}?kind=USERS`}>
                <IconExport />
                Export users activity data
              </Button>
            </ThemeProvider>
          </div>
        ) : null}

        <div styleName="mainPanel">
          <div styleName="header">
            <div styleName="checklistIcon">
              <IconChecklist />
            </div>
            Content Priorities &amp; Guidelines
          </div>
          <CollapsibleTab
            collapsed={tab !== 0}
            icon={<IconVideo styleName="icon20px" />}
            onExpand={() => {
              setTab(0);
            }}
            title="Video Requirements"
          >
            <div styleName="cards">
              <div styleName="card">
                <div styleName="cardTitle">Video Specs</div>
                <CheckedItem>Duration from 2 to 8 minutes</CheckedItem>
                <CheckedItem>One clear intention per video</CheckedItem>
                <CheckedItem>Include modifications for all levels</CheckedItem>
                <CheckedItem>Focus on authentic delivery</CheckedItem>
              </div>
              <div styleName="card">
                <div styleName="cardTitle">Content Essentials</div>
                <CheckedItem>State your intention clearly</CheckedItem>
                <CheckedItem>Mix breath, movement, stillness</CheckedItem>
                <CheckedItem>
                  Think &ldquo;work-in&rdquo; not workout
                </CheckedItem>
                <CheckedItem>Create healing spaces</CheckedItem>
              </div>
            </div>
          </CollapsibleTab>
          <CollapsibleTab
            collapsed={tab !== 1}
            icon={<IconCube />}
            onExpand={() => {
              setTab(1);
            }}
            title="Space Considerations"
          >
            <div styleName="cardContainer">
              <div styleName="cards2">
                <div styleName="card2">
                  <div styleName="cardTitle">Seated (3&times;3 ft)</div>
                  <p>Arms can not fully extend</p>
                </div>
                <div styleName="card2">
                  <div styleName="cardTitle">Standing Small (3&times;6 ft)</div>
                  <p>Limited arm movement</p>
                </div>
                <div styleName="card2">
                  <div styleName="cardTitle">Standing Full (6&times;6 ft)</div>
                  <p>Full range of motion</p>
                </div>
                <div styleName="card2">
                  <div styleName="cardTitle">Mat Space (8&times;8 ft)</div>
                  <p>Floor work possible</p>
                </div>
              </div>
              <div styleName="card2">
                <div styleName="cardTitle">Remember</div>
                <p>Tape off your filming space to match these dimensions</p>
              </div>
            </div>
          </CollapsibleTab>
          <CollapsibleTab
            collapsed={tab !== 2}
            icon={<IconDisplay styleName="icon20px" />}
            onExpand={() => {
              setTab(2);
            }}
            title="Equipment Options"
          >
            <p styleName="checklistText">
              Keep this as your checklist. Select all that apply:
            </p>
            <div styleName="checklist">
              <EquipmentItem>None needed</EquipmentItem>
              <EquipmentItem>Blocks</EquipmentItem>
              <EquipmentItem>Bolster</EquipmentItem>
              <EquipmentItem>Resistance bands</EquipmentItem>
              <EquipmentItem>Light weights</EquipmentItem>
              <EquipmentItem>Mat</EquipmentItem>
              <EquipmentItem>Other</EquipmentItem>
            </div>
          </CollapsibleTab>
        </div>
      </div>
      <div styleName="sidePanel">
        <div styleName="topBar">
          <div styleName="notificationsWrap">
            <button
              aria-label="Notifications"
              onClick={() => {
                setNotificationsOpen((prev) => !prev);
              }}
              ref={bellRef}
              styleName="notifications"
              type="button"
            >
              <IconBell />
            </button>
            {notificationsOpen ? (
              <NotificationsPanel
                anchorRef={bellRef}
                onClose={() => {
                  setNotificationsOpen(false);
                }}
              />
            ) : null}
          </div>
        </div>
        <div
          styleName={
            notificationsOpen ? 'sidePanelBody hiddenOnMobile' : 'sidePanelBody'
          }
        >
          <GreatContentTips />
          <SupportAndResources />
        </div>
      </div>
    </PageWithVideoBackground>
  );
};

export default CmsDashboard;
